import { Check, ChevronDown, Search } from "lucide-react";
import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { cn } from "../lib/cn.ts";
import { FIELD_BASE } from "./input.tsx";

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary text shown to the right — a phone, an email, a weekday. */
  hint?: string;
}

export interface ComboboxProps {
  id?: string;
  value: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  /** Shown on the trigger while nothing is selected. */
  placeholder?: string;
  /** Adds the filter box. Turn it off for short, fixed lists. */
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Marks the trigger for assistive tech; enforcement belongs to the form. */
  required?: boolean;
  /**
   * Lets the search text become a new option. Needed wherever the list is
   * built from what already exists — without it there'd be no way to enter
   * the first one.
   */
  creatable?: boolean;
  createLabel?: (query: string) => string;
  /** Nombre accesible cuando el campo no tiene una etiqueta visible al lado. */
  "aria-label"?: string;
  "aria-describedby"?: string;
  className?: string;
}

/**
 * A select we own, instead of the browser's.
 *
 * The native `<select>` renders as an OS widget: it can't be themed, it looks
 * different on every platform, and on mobile it takes over the screen. That
 * was fine for two-option lists and wrong for a patient list, which needs to
 * be searchable.
 *
 * The list is portalled rather than rendered inline: these fields live inside
 * a modal whose body scrolls, and an inline dropdown gets clipped by that
 * scroll container exactly when the list is long enough to matter. Where it
 * portals to, and why, is explained on the placement effect below.
 */
export function Combobox({
  id,
  value,
  options,
  onChange,
  placeholder = "Seleccionar…",
  searchable = false,
  searchPlaceholder = "Buscar…",
  emptyMessage = "Sin resultados.",
  disabled = false,
  invalid = false,
  required = false,
  creatable = false,
  createLabel = (q) => `Agregar «${q}»`,
  "aria-label": label,
  "aria-describedby": describedBy,
  className,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<{
    left: number;
    top: number;
    width: number;
    shift: number;
  } | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // A creatable field shows the raw value when it isn't in the list yet — the
  // name someone typed is still the answer, list or no list.
  const selected =
    options.find((option) => option.value === value) ??
    (creatable && value ? { value, label: value } : null);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = !q
      ? options
      : options.filter(
          (option) =>
            option.label.toLowerCase().includes(q) ||
            option.hint?.toLowerCase().includes(q),
        );
    const typed = query.trim();
    if (
      creatable &&
      typed &&
      !options.some((o) => o.label.toLowerCase() === typed.toLowerCase())
    )
      return [...matches, { value: typed, label: createLabel(typed) }];
    return matches;
  }, [options, query, creatable, createLabel]);

  /**
   * Where the list is mounted, and where its coordinates are measured from.
   *
   * Inside a dialog it mounts on the dialog itself, not on `document.body`.
   * A Radix dialog blocks pointer events outside its own subtree, traps focus
   * back into it, and dismisses on any outside pointerdown — a body-level list
   * would render perfectly and then refuse every click, steal nothing but
   * grief from the search box, and close the form when tapped.
   *
   * The dialog is `translate`d, and a transform makes it the containing block
   * for `position: fixed` children, so viewport coordinates have to be
   * rebased onto its box.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const node = triggerRef.current;
    const dialog = node?.closest<HTMLElement>('[role="dialog"]') ?? null;
    setHost(dialog ?? document.body);
    const place = () => {
      if (!node) return;
      const box = node.getBoundingClientRect();
      const below = window.innerHeight - box.bottom;
      // Estimación del alto para decidir si la lista abre hacia arriba: 36 por
      // renglón (py-2 sobre una línea de 20), 12 del relleno de la lista y 45
      // del buscador con su borde. El techo es el mismo `max-h-72` de la lista;
      // si se cambia uno hay que cambiar el otro o la lista abre torcida.
      const height = Math.min(
        288,
        filtered.length * 36 + (searchable ? 57 : 12),
      );
      // Flip above the field when the space below can't hold the list.
      const top =
        below < height && box.top > below
          ? box.top - height - 6
          : box.bottom + 6;
      const origin = dialog?.getBoundingClientRect();
      setRect({
        left: box.left - (origin?.left ?? 0),
        top: top - (origin?.top ?? 0),
        width: box.width,
        // Lo que hay que restarle si la lista, ya más ancha que el campo, se
        // pasa del borde derecho. Se calcula después de pintar, en el efecto
        // de abajo; acá se reinicia porque al mover o redimensionar la ventana
        // el desplazamiento anterior ya no corresponde.
        shift: 0,
      });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, filtered.length, searchable]);

  /**
   * Devuelve la lista a la pantalla cuando creció hacia afuera.
   *
   * El ancho ya no lo fija el campo sino el contenido, así que recién con la
   * lista pintada se sabe cuánto ocupa: un selector pegado al borde derecho
   * —el rol de cada miembro, al final de su renglón— abriría una lista con la
   * mitad de las opciones fuera de la ventana. Corre sólo cuando hace falta, y
   * el `shift` que escribe deja de haber sobrante, así que no se realimenta.
   */
  useLayoutEffect(() => {
    if (!open || !rect || !listRef.current) return;
    const box = listRef.current.getBoundingClientRect();
    const over = box.right - (window.innerWidth - 8);
    if (over > 0)
      setRect((current) =>
        current ? { ...current, shift: current.shift + over } : current,
      );
  }, [open, rect]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !listRef.current?.contains(target)
      )
        setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open]);

  /**
   * El buscador toma el foco al montarse, y no desde un efecto por `open`.
   *
   * El panel se dibuja recién cuando ya midió dónde ponerse (`open && rect &&
   * host`), así que en el render donde `open` pasa a true el `input` todavía
   * no existe: el efecto corría contra una referencia vacía y no volvía a
   * correr nunca. El foco quedaba en el botón, y las teclas que seguían —una
   * "á", un borrado— se perdían sin que nada lo explicara.
   */
  const focusSearch = (node: HTMLInputElement | null) => {
    searchRef.current = node;
    node?.focus();
  };

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  function commit(option: ComboboxOption) {
    onChange(option.value);
    setOpen(false);
    setQuery("");
    triggerRef.current?.focus();
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    /**
     * Escribir abre y busca, sin tener que abrir primero.
     *
     * Antes una letra sobre el campo no hacía nada: había que acertarle a
     * Enter o al clic y recién entonces se podía escribir. Con eso el campo se
     * comporta como un desplegable aunque por dentro acepte cualquier texto, y
     * quien lo usa termina buscando con el mouse una lista que en realidad se
     * filtra tipeando.
     *
     * Va antes del corte por `open` y mirando de dónde viene la tecla, no
     * dentro del caso cerrado: el foco tarda un cuadro en pasar del botón al
     * buscador, y en ese cuadro siguen llegando teclas **al botón**, ya con el
     * panel abierto. Atajarlas sólo estando cerrado dejaba la consulta en la
     * primera letra y perdía el resto —escribir "toxi" filtraba por "t"—.
     *
     * Se acumulan por el mismo motivo, y se sueltan en cuanto el buscador toma
     * el foco: desde ahí las teclas ya no pasan por acá.
     *
     * El espacio entra sólo con el panel ya abierto. Cerrado es la tecla que
     * activa un botón, y ésa es la convención que hay que respetar. Pero
     * abierto y sobre el botón, dejarlo pasar como activación cerraba el panel
     * a mitad de palabra y perdía lo escrito: "Drenaje linfático" quedaba en
     * "linfático", que es un tratamiento que no existe.
     */
    if (
      searchable &&
      event.target === triggerRef.current &&
      event.key.length === 1 &&
      (event.key !== " " || open) &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      event.preventDefault();
      setOpen(true);
      setQuery((current) => current + event.key);
      setActive(0);
      return;
    }
    if (!open) {
      if (["ArrowDown", "Enter", " "].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
        setQuery("");
        setActive(
          Math.max(
            0,
            filtered.findIndex((o) => o.value === value),
          ),
        );
        return;
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setQuery("");
      triggerRef.current?.focus();
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End") {
      event.preventDefault();
      setActive(filtered.length - 1);
    } else if (event.key === "Enter" || event.key === "Tab") {
      const option = filtered[active];
      if (option) {
        event.preventDefault();
        commit(option);
      }
    }
  }

  const listId = id ? `${id}-listbox` : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-label={label}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setQuery("");
          setActive(
            Math.max(
              0,
              filtered.findIndex((o) => o.value === value),
            ),
          );
        }}
        onKeyDown={onKeyDown}
        className={cn(
          FIELD_BASE,
          "flex h-10 items-center justify-between gap-2 text-left",
          invalid && "border-danger focus-visible:ring-danger",
          className,
        )}
      >
        <span className={cn("truncate", !selected && "text-muted-foreground")}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {open &&
        rect &&
        host &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            onKeyDown={onKeyDown}
            // Two guards, both needed when this opens inside a Radix dialog.
            //
            // The dialog blocks interaction with everything outside itself by
            // setting `pointer-events: none` on the page, and this list is
            // portalled to the body — outside. Without re-enabling them the
            // options render perfectly and simply cannot be clicked.
            //
            // The dialog also dismisses on any pointerdown it sees outside its
            // content, so an unstopped event here would close the whole form
            // instead of picking an option.
            onPointerDown={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            style={{
              position: "fixed",
              left: `${rect.left - rect.shift}px`,
              top: `${rect.top}px`,
              // El campo es el ancho mínimo, no el máximo. Atado a él, un
              // selector angosto —el de rol vive en una caja de 160— cortaba la
              // aclaración de cada opción justamente donde dice qué hace, que
              // es lo único que hay que leer para elegir. Ahora la lista crece
              // con su contenido; el techo es para que una lista de pacientes
              // con correos largos no se estire de punta a punta.
              minWidth: `${rect.width}px`,
              maxWidth: `min(28rem, calc(100vw - 1rem))`,
              pointerEvents: "auto",
            }}
            className="z-[200] w-max overflow-hidden rounded-xl border border-border bg-card shadow-xl"
          >
            {searchable && (
              <div className="flex items-center gap-2 border-b border-border px-3">
                <Search
                  className="h-4 w-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <input
                  ref={focusSearch}
                  value={query}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder}
                  onInput={(e) => {
                    setQuery((e.target as HTMLInputElement).value);
                    setActive(0);
                  }}
                  onKeyDown={onKeyDown}
                  className="h-11 w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                />
              </div>
            )}
            <div className="max-h-72 overflow-y-auto overscroll-contain p-1.5">
              {filtered.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </p>
              ) : (
                filtered.map((option, index) => (
                  <div
                    key={option.value}
                    data-index={index}
                    role="option"
                    aria-selected={option.value === value}
                    onPointerEnter={() => setActive(index)}
                    onClick={() => commit(option)}
                    className={cn(
                      // Dos estados que hay que poder distinguir de un vistazo:
                      // dónde está el cursor y qué está elegido. Antes los dos
                      // eran sutiles —un gris y un texto de color— y con la
                      // lista abierta costaba leer cuál era cuál. Ahora lo
                      // elegido es una superficie de acento, que es el papel
                      // que la lavanda tiene en la marca.
                      "flex cursor-pointer items-start gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors",
                      index === active &&
                        option.value !== value &&
                        "bg-muted text-foreground",
                      option.value === value &&
                        "bg-accent font-medium text-accent-foreground",
                    )}
                  >
                    {/* Reserva su lugar siempre: sin esto las etiquetas se
                        corren un tilde al cambiar de opción. */}
                    <Check
                      className={cn(
                        // El par acento/acento-primer-plano es el único que el
                        // sistema garantiza legible en los dos temas; `primary`
                        // está medido contra el fondo, no contra el acento.
                        "mt-0.5 h-4 w-4 shrink-0 text-accent-foreground",
                        option.value === value ? "opacity-100" : "opacity-0",
                      )}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {option.label}
                    </span>
                    {option.hint && (
                      <span
                        className={cn(
                          "mt-px shrink-0 text-xs",
                          option.value === value
                            ? "text-accent-foreground/70"
                            : "text-muted-foreground",
                        )}
                      >
                        {option.hint}
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>,
          host,
        )}
    </>
  );
}
